import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { CompanyContext, FieldDef } from '../../orbit/lib/dossier-types';
import { SectionCard } from './SectionCard';
import { sectionProgress } from './fieldKit';

/**
 * The onboarding card flow: one section at a time, centred, filled in already.
 *
 * The dossier has always held these answers, but the only ways to see them were
 * a one-question-at-a-time walk with no summary, or a settings page of side-by-
 * side blocks. Neither reads as "here is your company, confirm it". This is that
 * surface: four cards in the order the business is actually described — who you
 * are, what you run on, how you are organised, where you are going — each
 * opening from what we read, each saving as you type.
 *
 * The order is fixed rather than derived from the registry, because it is a
 * narrative, not a data listing: tools make more sense once we know the
 * industry, and goals make more sense once we know the teams.
 */

interface CardSpec {
  letter: string;
  title: string;
  blurb: string;
}

/** Written for someone who does not work here and does not use our words.
 *  The registry's own labels ("Stack", "Org") are internal vocabulary. */
const CARDS: CardSpec[] = [
  { letter: 'a', title: 'Your company',      blurb: 'What you do, where you do it, and roughly how big you are.' },
  { letter: 'b', title: 'The tools you use', blurb: 'The software your team already works in every day.' },
  { letter: 'c', title: 'Your teams',        blurb: 'The departments the company is organised into.' },
  { letter: 'd', title: "Where you're going", blurb: 'What you want to be true a year from now.' },
];

export interface SectionCardFlowProps {
  fields: FieldDef[];
  context: CompanyContext;
  onCommit: (key: string, value: unknown, action: 'confirm' | 'correct') => void | Promise<unknown>;
  /** Called when the last card is passed. The caller decides what comes next
   *  (in Orbit, offering a problem to scope). */
  onFinish: () => void;
  /** Per-section extra content above the fields — e.g. the stack card's tools. */
  renderSectionExtra?: (letter: string) => React.ReactNode;
  /** Per-section content below the fields — e.g. "add anything missing". */
  renderSectionFooter?: (letter: string) => React.ReactNode;
  /** Restrict/extend the sections shown. Defaults to Company/Stack/Teams/Goals. */
  cards?: CardSpec[];
  finishLabel?: string;
  /** A6 — a terminal "anything we missed" step, after the section cards and
   *  before whatever `onFinish` leads to. Counted in the position indicator. */
  renderCatchAll?: () => React.ReactNode;
  /** Open on this section rather than the first. Used when someone is sent back
   *  to finish a specific gap: landing them on card one to hunt for it is the
   *  same as not telling them. */
  startAtLetter?: string;
}

export const SectionCardFlow: React.FC<SectionCardFlowProps> = ({
  fields, context, onCommit, onFinish,
  renderSectionExtra, renderSectionFooter, cards = CARDS,
  finishLabel = 'Looks right',
  renderCatchAll, startAtLetter,
}) => {
  const [idx, setIdx] = useState(0);

  const fieldsBySection = useMemo(() => {
    const by = new Map<string, FieldDef[]>();
    for (const f of fields) {
      if ((f.layer || 'L1') !== 'L1') continue;
      const list = by.get(f.section) ?? [];
      list.push(f);
      by.set(f.section, list);
    }
    return by;
  }, [fields]);

  // A card with no fields at all would be a dead step, so drop it rather than
  // showing an empty card and asking the user to click past it.
  const steps = useMemo(
    () => cards.filter(c => (fieldsBySection.get(c.letter) ?? []).some(f => !f.hidden)),
    [cards, fieldsBySection],
  );

  // The catch-all sits one past the last section card, so it shares the same
  // index space, back button and indicator instead of being a separate screen.
  const catchAllIdx = renderCatchAll ? steps.length : -1;
  const lastIdx = Math.max(0, steps.length - 1 + (renderCatchAll ? 1 : 0));
  const clamped = Math.min(idx, lastIdx);
  const onCatchAll = clamped === catchAllIdx;
  const step = onCatchAll ? undefined : steps[clamped];

  // Follow the caller when it names a section to open on. Keyed on the letter so
  // arriving at the same gap twice does not fight the person's own paging.
  const jumpedTo = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!startAtLetter || jumpedTo.current === startAtLetter) return;
    const at = steps.findIndex(s => s.letter === startAtLetter);
    if (at < 0) return;
    jumpedTo.current = startAtLetter;
    setIdx(at);
  }, [startAtLetter, steps]);

  const valuesFor = useCallback(
    (letter: string) =>
      ((context as unknown as Record<string, Record<string, unknown> | undefined>)[
        `section_${letter}`
      ] ?? {}),
    [context],
  );

  const back = () => setIdx(i => Math.max(0, i - 1));
  const next = () => {
    if (clamped >= lastIdx) return onFinish();
    setIdx(clamped + 1);
  };

  if (!step && !onCatchAll) {
    // The schema has not landed yet, or every card's fields are hidden. Show the
    // shape of what is coming rather than an empty stage — this route renders
    // nothing else, so `null` would read as a broken screen.
    return (
      <div className="mx-auto w-full max-w-2xl" aria-busy="true">
        <div className="h-1.5 w-24 animate-pulse rounded-full bg-gray-200" />
        <div className="mt-6 h-48 animate-pulse rounded-2xl bg-gray-100" />
      </div>
    );
  }

  const stepFields = step ? (fieldsBySection.get(step.letter) ?? []) : [];
  const isLast = clamped === lastIdx;

  return (
    <div className="mx-auto w-full max-w-2xl">
      <ol className="mb-4 flex items-center justify-center gap-2" aria-label="Progress through your company profile">
        {renderCatchAll && (
          <li className="order-last">
            <button
              type="button"
              onClick={() => setIdx(catchAllIdx)}
              aria-current={onCatchAll ? 'step' : undefined}
              title="Anything we missed"
              className={clsx(
                'h-1.5 rounded-full transition-all',
                onCatchAll ? 'w-8 bg-brand-500' : 'w-4 bg-gray-200 hover:bg-gray-300',
              )}
            >
              <span className="sr-only">Anything we missed</span>
            </button>
          </li>
        )}
        {steps.map((s, i) => {
          const secFields = (fieldsBySection.get(s.letter) ?? []).filter(f => !f.hidden);
          const complete = sectionProgress(secFields, valuesFor(s.letter)).done;
          return (
            <li key={s.letter}>
              <button
                type="button"
                onClick={() => setIdx(i)}
                aria-current={i === clamped ? 'step' : undefined}
                title={s.title}
                className={clsx(
                  'h-1.5 rounded-full transition-all',
                  i === clamped ? 'w-8 bg-brand-500' : 'w-4',
                  i !== clamped && (complete ? 'bg-green-400' : 'bg-gray-200 hover:bg-gray-300'),
                )}
              >
                <span className="sr-only">{s.title}</span>
              </button>
            </li>
          );
        })}
      </ol>

      <p className="mb-3 text-center font-sans text-[10.5px] uppercase tracking-wider text-gray-400">
        {clamped + 1} of {lastIdx + 1}
      </p>

      {onCatchAll || !step ? renderCatchAll?.() : (
        <SectionCard
          letter={step.letter}
          title={step.title}
          blurb={step.blurb}
          fields={stepFields}
          values={valuesFor(step.letter)}
          fieldStates={context.field_states}
          onCommit={onCommit}
          autosave
          footer={renderSectionFooter?.(step.letter)}
        >
          {renderSectionExtra?.(step.letter)}
        </SectionCard>
      )}

      {/* No Save button anywhere on the card — every row saves itself, so these
          only move between cards. */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={back}
          disabled={clamped === 0}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={next}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700 whitespace-nowrap"
        >
          {isLast ? finishLabel : 'Next'}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default SectionCardFlow;
